<template>
  <div class="halaman-admin">
    <div class="dashboard-container">
      <div v-if="loading" class="progress-bar" :style="{ width: progress + '%' }"></div>

      <!-- Header & Page Actions -->
      <div class="header-section mb-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h4 class="page-main-title mb-1">
              <i class="bi bi-receipt-cutoff text-primary me-2"></i>Tagihan Pembayaran
            </h4>
            <p class="text-muted mb-0 small">
              Kelola tagihan pendaftaran kontingen, verifikasi bukti transfer, dan pantau status pelunasan atlet.
            </p>
          </div>

          <div class="d-flex flex-wrap gap-2">
            <router-link
              v-if="permissions && permissions.includes('create payment')"
              to="/admin/payment/buat"
              class="btn btn-primary btn-sm d-flex align-items-center gap-1.5 shadow-sm text-white fw-medium"
            >
              <i class="bi bi-plus-circle-fill"></i>
              <span>Buat Tagihan Baru</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Modern Filter Card -->
      <div class="modern-filter-card mb-4">
        <div class="filter-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div class="d-flex align-items-center gap-2">
            <div class="filter-icon-box">
              <i class="bi bi-sliders2 text-primary"></i>
            </div>
            <div>
              <span class="filter-title fw-semibold">Filter & Pencarian Tagihan</span>
              <span v-if="activeFilterCount > 0" class="badge bg-primary-subtle text-primary rounded-pill ms-2">
                {{ activeFilterCount }} Filter Aktif
              </span>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2">
            <button
              v-if="activeFilterCount > 0 || searchQuery"
              type="button"
              class="btn btn-link btn-sm text-decoration-none text-danger p-0 d-flex align-items-center gap-1"
              @click="resetFilter"
            >
              <i class="bi bi-arrow-counterclockwise"></i> Reset Filter
            </button>
          </div>
        </div>

        <!-- Main Filter Grid -->
        <div class="row g-3">
          <!-- Pencarian Cepat -->
          <div class="col-12 col-md-6 col-lg-4">
            <div class="filter-field">
              <label class="filter-label"><i class="bi bi-search text-muted me-1"></i>Pencarian Cepat</label>
              <div class="search-input-group">
                <i class="bi bi-search search-icon text-muted"></i>
                <input
                  type="text"
                  class="form-control search-input"
                  v-model="searchQuery"
                  placeholder="Cari no. invoice, bank, official..."
                  @input="onSearchInput"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="btn btn-link btn-clear-search text-muted p-0"
                  @click="clearSearch"
                >
                  <i class="bi bi-x-circle-fill"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Turnamen -->
          <div class="col-12 col-md-6 col-lg-4">
            <div class="filter-field">
              <label class="filter-label"><i class="bi bi-trophy text-muted me-1"></i>Turnamen / Acara</label>
              <select
                class="form-select filter-select"
                v-model="selectedTournamentId"
                @change="loadBillings(1)"
              >
                <option value="">Semua Turnamen</option>
                <option
                  v-for="tournament in allTournaments"
                  :key="tournament.id"
                  :value="tournament.id"
                >
                  {{ tournament.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Status Pembayaran -->
          <div class="col-12 col-md-6 col-lg-2">
            <div class="filter-field">
              <label class="filter-label"><i class="bi bi-credit-card text-muted me-1"></i>Status</label>
              <select
                class="form-select filter-select"
                v-model="selectedStatus"
                @change="loadBillings(1)"
              >
                <option value="">Semua Status</option>
                <option value="waiting for payment">Menunggu Pembayaran</option>
                <option value="waiting for confirmation">Menunggu Verifikasi</option>
                <option value="paid">Lunas</option>
                <option value="failed">Gagal</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
          </div>

          <!-- Tampilkan Per Halaman -->
          <div class="col-12 col-md-6 col-lg-2">
            <div class="filter-field">
              <label class="filter-label">Tampilkan</label>
              <select
                class="form-select filter-select"
                v-model="perPage"
                @change="loadBillings(1)"
              >
                <option :value="10">10 Data</option>
                <option :value="25">25 Data</option>
                <option :value="50">50 Data</option>
                <option :value="100">100 Data</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Bulk Action Bar -->
      <transition name="fade">
        <div v-if="tagihanDipilih.length > 0" class="alert alert-primary d-flex align-items-center justify-content-between py-2 px-3 mb-3 shadow-sm rounded-3">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-check2-circle fs-5 text-primary"></i>
            <span class="fw-semibold"><strong>{{ tagihanDipilih.length }}</strong> tagihan dipilih</span>
          </div>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="tagihanDipilih = []"
            >
              Batalkan Pilihan
            </button>
            <button
              type="button"
              class="btn btn-sm btn-danger d-flex align-items-center gap-1"
              :disabled="sedangMenghapusMassal"
              @click="hapusMassal"
            >
              <i class="bi" :class="sedangMenghapusMassal ? 'bi-arrow-repeat berputar' : 'bi-trash3-fill'"></i>
              <span>Hapus Tagihan Terpilih</span>
            </button>
          </div>
        </div>
      </transition>

      <!-- Modern Table Card -->
      <div class="modern-table-card mb-4">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0 tabel-tagihan">
            <thead>
              <tr>
                <th v-if="bolehHapusMassal" class="text-center" style="width: 44px">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isSemuaDipilih"
                    :indeterminate.prop="isSebagianDipilih"
                    @change="togglePilihSemua"
                    title="Pilih semua tagihan di halaman ini"
                  />
                </th>
                <th class="text-center" style="width: 50px">No</th>
                <th>Nomor Invoice</th>
                <th>Turnamen</th>
                <th>Official / Akun</th>
                <th class="text-end">Nominal & Bank</th>
                <th class="text-center">Status</th>
                <th class="text-end" style="width: 110px">Aksi</th>
              </tr>
            </thead>
            <!-- Loading State -->
            <tbody v-if="loading">
              <tr>
                <td :colspan="jumlahKolom" class="text-center py-5">
                  <div class="d-flex flex-column align-items-center justify-content-center py-4">
                    <div class="spinner-border text-primary mb-3" role="status" style="width: 2.5rem; height: 2.5rem;">
                      <span class="visually-hidden">Memuat...</span>
                    </div>
                    <div class="fw-semibold text-dark">Memuat data tagihan...</div>
                    <small class="text-muted">Mohon tunggu sebentar</small>
                  </div>
                </td>
              </tr>
            </tbody>

            <!-- Data Rows -->
            <tbody v-else-if="billings && billings.length > 0">
              <tr v-for="(billing, index) in billings" :key="billing.id" class="table-row-hover" :class="{ 'table-active': tagihanDipilih.includes(billing.id) }">
                <!-- Checkbox -->
                <td v-if="bolehHapusMassal" class="text-center c-cek" @click.stop>
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :value="billing.id"
                    v-model="tagihanDipilih"
                  />
                </td>

                <!-- No -->
                <td class="text-center text-muted fw-semibold c-no">
                  {{ index + 1 + (currentPage - 1) * perPage }}
                </td>

                <!-- Invoice -->
                <td class="c-inv">
                  <div class="d-flex align-items-center gap-2">
                    <div class="invoice-icon-box">
                      <i class="bi bi-file-earmark-text text-primary"></i>
                    </div>
                    <div>
                      <span class="fw-bold text-dark d-block text-nowrap">{{ billing.invoice_number }}</span>
                      <small class="text-muted d-block" v-if="billing.created_at">
                        <i class="bi bi-calendar3 me-1"></i>{{ formatTanggal(billing.created_at) }}
                      </small>
                    </div>
                  </div>
                </td>

                <!-- Tournament -->
                <td class="kolom-turnamen c-tur">
                  <span class="fw-medium text-dark d-block teks-potong" :title="billing.tournament?.name">
                    {{ billing.tournament?.name || '-' }}
                  </span>
                  <small class="text-muted" v-if="billing.billing_details_count">
                    <i class="bi bi-people me-1"></i>{{ billing.billing_details_count }} Atlet Terdaftar
                  </small>
                </td>

                <!-- User / Official -->
                <td class="c-off">
                  <div v-if="billing.user">
                    <span class="fw-medium text-dark d-block teks-potong" :title="billing.user.name">{{ billing.user.name }}</span>
                    <small class="text-muted d-inline-block text-truncate" style="max-width: 160px" :title="billing.user.email">{{ billing.user.email }}</small>
                  </div>
                  <span v-else class="text-muted">-</span>
                </td>

                <!-- Nominal + bank -->
                <td class="text-end c-nom">
                  <span class="fw-bold text-dark d-block text-nowrap">{{ formatNumber(billing.amount) }}</span>
                  <small class="text-muted text-nowrap"><i class="bi bi-bank me-1"></i>{{ billing.bank_name || 'Transfer Bank' }}</small>
                </td>

                <!-- Status Badge -->
                <td class="text-center c-st">
                  <span :class="['badge-status', getStatusClass(billing.status)]">
                    <i :class="getStatusIcon(billing.status)" class="me-1"></i>
                    {{ formatStatusLabel(billing.status) }}
                  </span>
                </td>

                <!-- Action Column -->
                <td class="text-end c-aksi">
                  <div class="d-flex gap-1.5 justify-content-end align-items-center">
                    <router-link
                      :to="`/admin/payment/${billing.id}`"
                      class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 px-2 py-1"
                      title="Lihat Detail Tagihan"
                    >
                      <i class="bi bi-eye"></i>
                      <span class="d-none d-xxl-inline">Lihat</span>
                    </router-link>

                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-2 py-1"
                      v-if="dapatDihapus(billing)"
                      title="Hapus Tagihan"
                      @click="hapusTagihan(billing)"
                    >
                      <i class="bi bi-trash3"></i>
                    </button>

                    <MenuAksi v-slot="{ tutup }">
                      <button
                        type="button"
                        class="aksi__item"
                        @click="tutup(); unduhInvoice(billing)"
                      >
                        <i class="bi bi-file-earmark-pdf text-danger"></i> Cetak Invoice
                      </button>

                      <button
                        type="button"
                        class="aksi__item"
                        v-if="billing.status === 'paid'"
                        @click="tutup(); unduhKwitansi(billing)"
                      >
                        <i class="bi bi-patch-check-fill text-success"></i> Cetak Kwitansi
                      </button>

                      <button
                        type="button"
                        class="aksi__item"
                        v-if="permissions && permissions.includes('edit payment')"
                        @click="tutup(); EditPayment(billing.id)"
                      >
                        <i class="bi bi-pencil-square text-primary"></i> Ubah Data Tagihan
                      </button>

                      <button
                        type="button"
                        class="aksi__item"
                        v-if="permissions && permissions.includes('upload payment struk') && billing.status !== 'paid'"
                        @click="tutup(); ConfirmPayment(billing.id)"
                      >
                        <i class="bi bi-upload text-info"></i> Unggah Bukti Transfer
                      </button>

                      <button
                        type="button"
                        class="aksi__item"
                        v-if="permissions && permissions.includes('confirm payment')"
                        @click="tutup(); ConfirmPayment(billing.id)"
                      >
                        <i class="bi bi-check2-circle text-success"></i> Verifikasi Pembayaran
                      </button>

                      <button
                        type="button"
                        class="aksi__item text-danger"
                        v-if="dapatDihapus(billing)"
                        @click="tutup(); hapusTagihan(billing)"
                      >
                        <i class="bi bi-trash3 text-danger"></i> Hapus Tagihan
                      </button>
                    </MenuAksi>
                  </div>
                </td>
              </tr>
            </tbody>

            <!-- Error State -->
            <tbody v-else-if="errorMessage">
              <tr>
                <td :colspan="jumlahKolom" class="text-center py-5">
                  <div class="empty-state-box">
                    <i class="bi bi-exclamation-triangle text-danger empty-icon mb-3 d-block"></i>
                    <h6 class="fw-bold text-dark mb-1">Gagal Memuat Data</h6>
                    <p class="text-muted small mb-3">{{ errorMessage }}</p>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="loadBillings(1)"
                    >
                      <i class="bi bi-arrow-clockwise me-1"></i> Coba Lagi
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>

            <!-- Empty State -->
            <tbody v-else>
              <tr>
                <td :colspan="jumlahKolom" class="text-center py-5">
                  <div class="empty-state-box">
                    <i class="bi bi-receipt empty-icon text-muted mb-3 d-block"></i>
                    <h6 class="fw-bold text-dark mb-1">Tidak Ada Data Tagihan</h6>
                    <p class="text-muted small mb-3">
                      Tidak ditemukan data tagihan pembayaran sesuai kriteria pencarian dan filter yang dipilih.
                    </p>
                    <button
                      v-if="activeFilterCount > 0 || searchQuery"
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      @click="resetFilter"
                    >
                      <i class="bi bi-arrow-counterclockwise me-1"></i> Reset Filter
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Table Footer / Pagination -->
        <div
          v-if="billings && billings.length > 0"
          class="table-footer d-flex flex-wrap justify-content-between align-items-center gap-3 px-3 py-3 border-top bg-light-subtle"
        >
          <div class="text-muted small">
            Menampilkan <strong>{{ (currentPage - 1) * perPage + 1 }}</strong> -
            <strong>{{ Math.min(currentPage * perPage, totalData) }}</strong> dari
            <strong>{{ totalData }}</strong> data tagihan
          </div>

          <AppPagination
            :current-page="currentPage"
            :total-pages="totalPages"
            :prev-page-url="prevPageUrl"
            :next-page-url="nextPageUrl"
            @change-page="changePage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import MenuAksi from "@/components/ui/MenuAksi.vue";
import AppPagination from "@/components/ui/AppPagination.vue";
import { inject } from "vue";
import { useToast } from "vue-toastification";
import { useConfirm } from "@/composables/useConfirm";

export default {
  name: "PaymentList",

  components: { MenuAksi, AppPagination },

  data() {
    return {
      billings: [],
      allTournaments: [],
      searchQuery: "",
      searchTimeout: null,
      currentPage: 1,
      perPage: 10,
      totalData: 0,
      totalPages: 0,
      prevPageUrl: null,
      nextPageUrl: null,
      loading: false,
      errorMessage: null,
      progress: 0,
      selectedStatus: "",
      selectedTournamentId: "",
      tagihanDipilih: [],
      sedangMenghapusMassal: false,
    };
  },

  setup() {
    const permissions = inject("permissions", []);
    const { konfirmasi } = useConfirm();
    const toast = useToast();
    return { permissions, konfirmasi, toast };
  },

  computed: {
    activeFilterCount() {
      let count = 0;
      if (this.selectedTournamentId) count++;
      if (this.selectedStatus) count++;
      return count;
    },

    isSemuaDipilih() {
      if (!this.billings || this.billings.length === 0) return false;
      return this.billings.every((b) => this.tagihanDipilih.includes(b.id));
    },

    isSebagianDipilih() {
      if (!this.billings || this.billings.length === 0) return false;
      const hitung = this.billings.filter((b) => this.tagihanDipilih.includes(b.id)).length;
      return hitung > 0 && hitung < this.billings.length;
    },

    userGroup() {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return u.group?.name || '';
      } catch (e) {
        return '';
      }
    },

    isPanitia() {
      return ['Owner', 'Admin', 'Event PIC'].includes(this.userGroup);
    },

    /** Bendahara hanya membaca dan menyetujui, jadi kotak pilih untuk hapus massal disembunyikan. */
    bolehHapusMassal() {
      return this.userGroup !== 'Bendahara';
    },

    jumlahKolom() {
      return this.bolehHapusMassal ? 8 : 7;
    },
  },

  mounted() {
    // Dibuka dari halaman Keuangan: langsung tersaring ke kejuaraan & status yang sama.
    const { tournament_id: idTurnamen, status } = this.$route.query;
    if (idTurnamen) this.selectedTournamentId = String(idTurnamen);
    if (status) this.selectedStatus = String(status);

    this.fetchAllTournaments();
    this.loadBillings();
  },

  methods: {
    async loadBillings(page = 1) {
      this.loading = true;
      this.errorMessage = null;
      try {
        const response = await http.get("/billings", {
          params: {
            page,
            perPage: this.perPage,
            per_page: this.perPage,
            search: this.searchQuery.trim(),
            status: this.selectedStatus,
            tournament_id: this.selectedTournamentId,
          },
        });

        const { current_page, last_page, data, next_page_url, prev_page_url, total } = response.data;
        this.billings = data || [];
        this.currentPage = current_page || 1;
        this.totalPages = last_page || 1;
        this.totalData = total != null ? total : (this.billings ? this.billings.length : 0);
        this.nextPageUrl = next_page_url;
        this.prevPageUrl = prev_page_url;
      } catch (error) {
        console.error("Error loading billings:", error);
        this.errorMessage = "Gagal memuat data tagihan. Silakan periksa koneksi Anda dan coba lagi.";
      } finally {
        this.loading = false;
      }
    },

    async fetchAllTournaments() {
      try {
        const response = await http.get("/tournaments", {
          params: { per_page: 100 }
        });
        this.allTournaments = response.data?.data || response.data || [];
      } catch (error) {
        console.error("Failed to fetch tournaments:", error);
      }
    },

    setStatusFilter(status) {
      this.selectedStatus = status;
      this.loadBillings(1);
    },

    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadBillings(1);
      }, 350);
    },

    clearSearch() {
      this.searchQuery = "";
      this.loadBillings(1);
    },

    resetFilter() {
      this.searchQuery = "";
      this.selectedStatus = "";
      this.selectedTournamentId = "";
      this.perPage = 10;
      this.loadBillings(1);
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.loadBillings(page);
      }
    },

    formatNumber(value) {
      if (value == null) return "-";
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    formatTanggal(isoString) {
      if (!isoString) return "-";
      try {
        const d = new Date(isoString);
        return new Intl.DateTimeFormat("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(d);
      } catch (e) {
        return isoString;
      }
    },

    formatStatusLabel(status) {
      const map = {
        paid: "Lunas",
        "waiting for payment": "Menunggu Bayar",
        "waiting for confirmation": "Menunggu Verifikasi",
        failed: "Gagal",
        cancelled: "Dibatalkan",
      };
      return map[status] || status || "-";
    },

    getStatusClass(status) {
      const map = {
        paid: "badge-status--paid",
        "waiting for payment": "badge-status--pending",
        "waiting for confirmation": "badge-status--review",
        failed: "badge-status--danger",
        cancelled: "badge-status--danger",
      };
      return map[status] || "badge-status--pending";
    },

    getStatusIcon(status) {
      const map = {
        paid: "bi bi-check-circle-fill",
        "waiting for payment": "bi bi-clock-history",
        "waiting for confirmation": "bi bi-hourglass-split",
        failed: "bi bi-x-circle-fill",
        cancelled: "bi bi-slash-circle-fill",
      };
      return map[status] || "bi bi-circle";
    },

    EditPayment(id) {
      this.$router.push({ name: "EditPayment", params: { id } });
    },

    ConfirmPayment(id) {
      this.$router.push({ name: "ConfirmPayment", params: { id } });
    },

    async unduhInvoice(billing) {
      try {
        const response = await http.get(`/billings/${billing.id}/invoice-pdf?t=${Date.now()}`, {
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Invoice-${billing.invoice_number || billing.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error downloading invoice:", error);
        this.toast.error("Gagal mengunduh invoice.");
      }
    },

    async unduhKwitansi(billing) {
      try {
        const response = await http.get(`/billings/${billing.id}/kwitansi-pdf?t=${Date.now()}`, {
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Kwitansi-${billing.invoice_number || billing.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error downloading kwitansi:", error);
        this.toast.error("Gagal mengunduh kwitansi.");
      }
    },

    dapatDihapus(billing) {
      if (!billing?.id) return false;
      if (this.userGroup === 'Bendahara') return false;
      if (this.isPanitia) return true;
      return billing.status !== "paid";
    },

    togglePilihSemua(e) {
      if (e.target.checked) {
        const ids = (this.billings || []).map((b) => b.id);
        this.tagihanDipilih = Array.from(new Set([...this.tagihanDipilih, ...ids]));
      } else {
        const idsHalaman = new Set((this.billings || []).map((b) => b.id));
        this.tagihanDipilih = this.tagihanDipilih.filter((id) => !idsHalaman.has(id));
      }
    },

    async hapusTagihan(billing) {
      const ok = await this.konfirmasi({
        title: "Hapus Tagihan Ini?",
        message: `Tagihan ${billing.invoice_number} akan dihapus secara permanen. Seluruh atlet/tim yang terdaftar di tagihan ini akan kembali berstatus belum ditagih sehingga dapat dibuatkan tagihan baru.`,
        confirmText: "Ya, Hapus Tagihan",
        tone: "danger",
      });

      if (!ok) return;

      try {
        const res = await http.delete(`/billings/${billing.id}`);
        this.toast.success(res.data?.message || "Tagihan berhasil dihapus.");
        this.tagihanDipilih = this.tagihanDipilih.filter((id) => id !== billing.id);
        this.loadBillings(this.currentPage);
      } catch (error) {
        const msg = error.response?.data?.message || "Gagal menghapus tagihan.";
        this.toast.error(msg);
      }
    },

    async hapusMassal() {
      if (!this.tagihanDipilih.length) return;

      const ok = await this.konfirmasi({
        title: `Hapus ${this.tagihanDipilih.length} Tagihan Terpilih?`,
        message: "Tagihan yang dipilih akan dihapus secara permanen. Atlet/tim di dalam tagihan yang dihapus akan kembali berstatus belum ditagih.",
        confirmText: "Ya, Hapus Semua Terpilih",
        tone: "danger",
      });

      if (!ok) return;

      this.sedangMenghapusMassal = true;
      try {
        const res = await http.post("/billings/bulk-destroy", {
          ids: this.tagihanDipilih,
        });
        this.toast.success(res.data?.message || "Tagihan terpilih berhasil dihapus.");
        this.tagihanDipilih = [];
        this.loadBillings(this.currentPage);
      } catch (error) {
        const msg = error.response?.data?.message || "Gagal menghapus tagihan terpilih.";
        this.toast.error(msg);
      } finally {
        this.sedangMenghapusMassal = false;
      }
    },
  },
};
</script>

<style scoped>

.page-main-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
}

/* ── Modern Filter Card ── */
.filter-card,
.modern-filter-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease;
}

.filter-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--accent-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.filter-title {
  font-size: 0.95rem;
  color: var(--text-primary);
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.filter-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.filter-select {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 9px;
  border: 1px solid var(--border-strong);
  background-color: var(--bg-surface);
  color: var(--text-primary);
  transition: all 0.15s ease;
}

.filter-select:focus {
  background-color: var(--bg-surface);
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-subtle);
}

/* ── Quick Status Pills ── */
.quick-status-pills {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 6px;
  gap: 0.5rem;
  scrollbar-width: none;
}

.quick-status-pills::-webkit-scrollbar {
  display: none;
}

.status-pill {
  white-space: nowrap;
  flex-shrink: 0;
  border: 1px solid var(--border-subtle);
  background: var(--bg-sunken);
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.35rem 0.85rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.status-pill:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.status-pill.active {
  background: var(--accent);
  color: #ffffff;
  border-color: var(--accent);
  box-shadow: var(--shadow-sm);
}

.status-pill--paid.active {
  background: #10b981 !important;
  color: #ffffff !important;
  border-color: #059669 !important;
}

.status-pill--pending.active {
  background: #f59e0b !important;
  color: #ffffff !important;
  border-color: #d97706 !important;
}

.status-pill--review.active {
  background: #0284c7 !important;
  color: #ffffff !important;
  border-color: #0369a1 !important;
}

.status-pill--danger.active {
  background: #ef4444 !important;
  color: #ffffff !important;
  border-color: #dc2626 !important;
}

@media (max-width: 640px) {
  .header-section > .d-flex {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.75rem !important;
  }
  .header-section .btn {
    width: 100%;
    justify-content: center;
  }
}

/* ── Search Input Group ── */
.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 2;
}

.search-input {
  padding-left: 2.5rem !important;
  padding-right: 2.2rem !important;
  border-radius: 9px;
  font-size: 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-strong);
  color: var(--text-primary);
}

.search-input:focus {
  background: var(--bg-surface);
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-subtle);
}

.btn-clear-search {
  position: absolute;
  right: 10px;
  font-size: 0.9rem;
}

/* ── Table Styling ── */
.modern-table-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  overflow-x: auto !important;
  box-shadow: var(--shadow-sm);
}

.modern-table-card .table th {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  background: var(--bg-sunken);
  padding: 0.85rem 0.75rem;
  border-bottom: 1px solid var(--border-subtle);
}

.modern-table-card .table td {
  padding: 0.85rem 0.75rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-subtle);
}

.table-row-hover:hover {
  background-color: var(--bg-hover);
}

.invoice-icon-box {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  background: var(--accent-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.bank-pill {
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 0.2rem 0.55rem;
  color: var(--text-secondary);
}

/* ── Badge Statuses ── */
.badge-status {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 20px;
  letter-spacing: 0.2px;
}

.badge-status--paid {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.badge-status--pending {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.badge-status--review {
  background: rgba(2, 132, 199, 0.15);
  color: #0284c7;
  border: 1px solid rgba(2, 132, 199, 0.3);
}

.badge-status--danger {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.empty-state-box {
  max-width: 380px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 2.8rem;
  opacity: 0.4;
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 4px;
  background: var(--grad-flame-soft);
  animation: loader-animation 1.5s infinite;
}

@keyframes loader-animation {
  0% { width: 0; }
  50% { width: 50%; }
  100% { width: 100%; }
}
</style>
<style scoped>
/* Muat tanpa geser di laptop: kolom panjang dibatasi lebarnya. */
.kolom-turnamen > * { display: block; max-width: 150px; white-space: normal; }
/* Teks panjang dipotong dengan "…" (nama lengkap muncul saat kursor diarahkan). */
.teks-potong { max-width: 170px; white-space: nowrap !important; overflow: hidden; text-overflow: ellipsis; }
.kolom-turnamen > .teks-potong { white-space: nowrap; }
.c-off small { display: block; }
@media (max-width: 1500px) { .kolom-turnamen .fw-medium { font-size: .82rem; } }

/* HP & tablet kecil: tiap tagihan jadi kartu, tidak ada kolom yang terpotong. */
@media (max-width: 767.98px) {
  .tabel-tagihan thead { display: none; }
  .tabel-tagihan, .tabel-tagihan tbody { display: block; width: 100%; min-width: 0 !important; }
  .tabel-tagihan tbody tr {
    display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: .5rem .75rem; width: 100%;
    grid-template-areas: "cek inv st" "cek tur nom" "cek off off" "aksi aksi aksi";
    padding: .85rem; height: auto !important; border-bottom: 1px solid var(--border-subtle, #e5e7eb);
  }
  .tabel-tagihan tbody td { display: block; height: auto !important; padding: 0 !important; border: 0 !important; text-align: left !important; min-width: 0; }
  .tabel-tagihan .c-no { display: none; }
  .tabel-tagihan .c-cek { grid-area: cek; }
  .tabel-tagihan .c-inv { grid-area: inv; }
  .tabel-tagihan .c-st { grid-area: st; justify-self: end; }
  .tabel-tagihan .c-tur { grid-area: tur; }
  .tabel-tagihan .c-tur > * { max-width: none; }
  .tabel-tagihan .c-nom { grid-area: nom; text-align: right !important; }
  .tabel-tagihan .c-off { grid-area: off; }
  .tabel-tagihan .c-aksi { grid-area: aksi; padding-top: .5rem !important; border-top: 1px dashed var(--border-subtle, #e5e7eb) !important; }
  .tabel-tagihan .c-aksi > div { display: flex !important; flex-direction: row !important; align-items: center !important; justify-content: flex-end !important; gap: .5rem !important; }
  .tabel-tagihan .c-aksi .btn, .tabel-tagihan .c-aksi :deep(.aksi__pemicu) { width: auto !important; flex: 0 0 auto !important; min-height: 36px; margin: 0 !important; }
  .tabel-tagihan .c-aksi > div > a { margin-right: auto !important; }
  .tabel-tagihan .c-inv > div { flex-wrap: nowrap; }
  .tabel-tagihan .c-aksi .d-xxl-inline { display: inline !important; }
  .tabel-tagihan tbody tr:not(:has(td.c-inv)) { display: block; }
  .tabel-tagihan tbody tr:not(:has(td.c-inv)) td { text-align: center !important; padding: 1.5rem !important; }
}
</style>
